import React, { useEffect, useState } from 'react';
import { ArrowUpRight, Star, Mail, TrendingUp, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import AuditModal from './AuditModal';
import { profileData } from '../mock';

const cyclingWords = ['Revenue', 'Growth', 'Results'];

const Hero = () => {
  const [wordIndex, setWordIndex] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setWordIndex((i) => (i + 1) % cyclingWords.length), 2400);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="relative pt-32 pb-20 overflow-hidden" data-testid="hero-section">
      {/* Decorative grid background */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.4] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(10,10,10,0.07) 1px, transparent 0)',
          backgroundSize: '32px 32px',
          maskImage: 'radial-gradient(circle at 50% 30%, black 40%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(circle at 50% 30%, black 40%, transparent 75%)',
        }}
      />
      {/* Decorative orange blob */}
      <div
        aria-hidden
        className="absolute -top-32 -right-24 w-[40rem] h-[40rem] rounded-full bg-[var(--gh-orange)] opacity-[0.07] blur-3xl"
      />

      <div className="container-x relative grid lg:grid-cols-12 gap-10 items-center">
        {/* Left: copy */}
        <div className="lg:col-span-7">
          <span
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cream-2 border border-line text-xs font-semibold uppercase tracking-[0.2em] text-ink"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--gh-orange)] animate-pulse" />
            Trusted by 60+ creators · 5.0 ★ on Upwork
          </span>

          <h1
            data-testid="hero-headline"
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold mt-6 leading-[0.98] text-ink tracking-tighter"
          >
            Turn email into your most predictable
            <br />
            <span className="relative inline-flex h-[1.05em] mt-3 align-bottom">
              <span className="invisible whitespace-nowrap font-serif italic pr-4">
                {cyclingWords.reduce((a, b) => (a.length > b.length ? a : b))}.
              </span>
              {cyclingWords.map((w, i) => (
                <span
                  key={w}
                  aria-hidden={wordIndex !== i}
                  className={`absolute inset-0 font-serif italic text-[var(--gh-orange)] whitespace-nowrap transition-all duration-500 ${
                    wordIndex === i
                      ? 'opacity-100 translate-y-0'
                      : i < wordIndex
                      ? 'opacity-0 -translate-y-full'
                      : 'opacity-0 translate-y-full'
                  }`}
                >
                  {w}.
                </span>
              ))}
            </span>
          </h1>

          <p className="mt-7 text-lg md:text-xl text-muted-warm max-w-xl leading-relaxed">
            We design and run email-first growth systems for solopreneurs, course creators
            and boutique brands — turning subscriber lists into revenue you can forecast.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => setOpen(true)}
              data-testid="hero-audit-cta"
              className="btn-orange"
            >
              Get a free email audit <ArrowUpRight className="w-4 h-4" />
            </button>
            <Link to="/case-studies" data-testid="hero-case-studies-cta" className="btn-outline">
              View case studies
            </Link>
          </div>

          {/* Stat chips */}
          <div className="mt-12 grid grid-cols-3 gap-3 sm:gap-5 max-w-xl">
            <div className="flex flex-col">
              <span className="font-display text-3xl md:text-4xl font-extrabold text-ink">200%</span>
              <span className="text-xs uppercase tracking-widest text-muted-warm mt-1">Open Rate Boost</span>
            </div>
            <div className="flex flex-col border-x border-line px-4">
              <span className="font-display text-3xl md:text-4xl font-extrabold text-ink">10–100×</span>
              <span className="text-xs uppercase tracking-widest text-muted-warm mt-1">Revenue Growth</span>
            </div>
            <div className="flex flex-col">
              <span className="font-display text-3xl md:text-4xl font-extrabold text-ink">2,000+</span>
              <span className="text-xs uppercase tracking-widest text-muted-warm mt-1">Lead-Magnet Downloads</span>
            </div>
          </div>
        </div>

        {/* Right: portrait + floating cards */}
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] max-w-md mx-auto rounded-[2rem] overflow-hidden bg-cream-2 ring-1 ring-line">
            <img
              src={profileData.image}
              alt="Akash Tiwari, Growth Hacking founder"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.src =
                  'https://images.unsplash.com/photo-1649433658557-54cf58577c68?w=900&q=80';
              }}
            />
            {/* warm gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--gh-orange)]/15 via-transparent to-transparent" />
          </div>

          {/* Card 1: Upwork rating */}
          <div
            data-testid="hero-card-upwork"
            className="absolute top-6 -left-2 sm:-left-6 float-card px-4 py-3 w-44 animate-float-y"
          >
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-xl bg-[var(--gh-orange-soft)] text-[var(--gh-orange)] flex items-center justify-center">
                <Star className="w-4 h-4 fill-[var(--gh-orange)]" />
              </span>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-warm">Upwork</p>
                <p className="font-bold text-ink leading-tight">5.0 ★ rating</p>
              </div>
            </div>
            <div className="mt-2 flex">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className="w-3 h-3 text-[var(--gh-orange)] fill-[var(--gh-orange)]" />
              ))}
            </div>
          </div>

          {/* Card 2: Reviews */}
          <div
            data-testid="hero-card-reviews"
            className="absolute bottom-20 -right-2 sm:-right-6 float-card px-4 py-3 w-52 animate-float-y-slow"
            style={{ animationDelay: '0.6s' }}
          >
            <p className="text-[10px] uppercase tracking-widest text-muted-warm">Verified reviews</p>
            <p className="font-display text-2xl font-extrabold text-ink leading-none mt-1">16</p>
            <p className="text-xs text-muted-warm mt-1">five-star outcomes from real clients</p>
            <div className="mt-3 flex -space-x-2">
              {['EH', 'JS', 'AM', 'KP'].map((i) => (
                <span
                  key={i}
                  className="w-6 h-6 rounded-full bg-cream-2 border-2 border-white text-[10px] font-bold flex items-center justify-center text-ink"
                >
                  {i}
                </span>
              ))}
              <span className="w-6 h-6 rounded-full bg-[var(--gh-orange)] border-2 border-white text-[10px] font-bold flex items-center justify-center text-white">
                +12
              </span>
            </div>
          </div>

          {/* Card 3: Open rate uplift */}
          <div
            data-testid="hero-card-openrate"
            className="absolute bottom-2 -left-1 sm:left-6 float-card px-4 py-3 w-56"
            style={{ animationDelay: '1.2s' }}
          >
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-xl bg-ink text-[var(--gh-cream)] flex items-center justify-center">
                <TrendingUp className="w-4 h-4" />
              </span>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-warm">Avg uplift</p>
                <p className="font-bold text-ink leading-tight">+212% open rate</p>
              </div>
            </div>
            {/* mini sparkline */}
            <svg viewBox="0 0 120 30" className="w-full mt-2">
              <polyline
                fill="none"
                stroke="var(--gh-orange)"
                strokeWidth="2"
                points="0,24 18,20 35,22 55,16 72,12 90,8 110,4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <polyline
                fill="none"
                stroke="var(--gh-ink)"
                strokeOpacity="0.25"
                strokeWidth="1.5"
                points="0,26 18,25 35,24 55,22 72,21 90,20 110,18"
              />
            </svg>
          </div>

          {/* Card 4: tiny "available" pill */}
          <div className="absolute -top-3 right-6 hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-ink text-[var(--gh-cream)] text-[11px] font-semibold">
            <Sparkles className="w-3 h-3 text-[var(--gh-orange)]" />
            Available · Q1 2026
          </div>
        </div>
      </div>

      <AuditModal isOpen={open} onClose={() => setOpen(false)} />
    </section>
  );
};

export default Hero;
